import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Alert, Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import { CSVLink } from 'react-csv';

import { columnSearchWidget } from 'components';
import {
  clearAssetEodPreview,
  clearAssetIntradayPreview,
  fetchSyntheticBondCatalogue,
  requestAssetEodPreview,
  requestAssetIntradayPreview,
  selectors,
  SyntheticBondCatalogueEntry,
} from 'state';
import AssetEodPreviewModal from '../Data/AssetsTab/AssetEodPreviewModal';
import AssetIntradayPreviewModal from '../Data/AssetsTab/AssetIntradayPreviewModal';

export type SyntheticBondCatalogueTableRow = SyntheticBondCatalogueEntry & {
  asset_search: string;
  currency_search: string;
  eod_search: string;
  initial_tenor: string;
  reference_curve_search: string;
  series_search: string;
};

const display = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '—' : String(value);

const compareText = (left: unknown, right: unknown) =>
  String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const compareOptionalNumber = (left: number | null, right: number | null) => {
  if (left === right) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
};

const uniqueFilters = <T extends string | number>(
  rows: SyntheticBondCatalogueTableRow[],
  value: (row: SyntheticBondCatalogueTableRow) => T,
  label: (row: SyntheticBondCatalogueTableRow) => string,
) =>
  Array.from(
    new Map(rows.map(row => [value(row), label(row)] as const)).entries(),
  )
    .map(([filterValue, text]) => ({ text, value: filterValue }))
    .sort((left, right) => compareText(left.text, right.text));

export const catalogueTableRows = (
  entries: SyntheticBondCatalogueEntry[],
): SyntheticBondCatalogueTableRow[] =>
  entries.map(entry => ({
    ...entry,
    asset_search: [entry.underlying_asset_id, entry.name]
      .filter(value => value !== null && value !== undefined && value !== '')
      .join(' '),
    currency_search: [entry.currency.symbol, entry.currency.name].join(' '),
    eod_search: `${entry.market_history_row_count} rows ${
      entry.market_history_row_count > 0 ? 'available' : 'unavailable'
    }`,
    initial_tenor: `${entry.initial_tenor_years}Y`,
    reference_curve_search: [
      entry.reference_curve.code,
      entry.reference_curve.label,
    ].join(' '),
    series_search: [
      entry.series_code,
      entry.family_label,
      `${entry.series_member_count} bonds`,
      entry.series_active ? 'active' : 'inactive',
    ]
      .filter(Boolean)
      .join(' '),
  }));

export const syntheticBondCatalogueColumns = (
  previewEod: (assetId: number) => void,
  previewingAssetId?: number,
  rows: SyntheticBondCatalogueTableRow[] = [],
  previewIntraday?: (assetId: number) => void,
  previewingIntradayAssetId?: number,
): ColumnsType<SyntheticBondCatalogueTableRow> => [
  {
    title: 'Series',
    dataIndex: 'series_search',
    key: 'series',
    fixed: 'left',
    width: 190,
    sorter: (a, b) => compareText(a.series_code, b.series_code),
    ...columnSearchWidget({ dataIndex: 'series_search' }),
    render: (_value, row) => (
      <Space orientation="vertical" size={0}>
        <Space size="small">
          <Typography.Text strong>{row.series_code}</Typography.Text>
          <Tag color={row.series_active ? 'green' : 'default'}>
            {row.series_active ? 'Active' : 'Inactive'}
          </Tag>
        </Space>
        <Typography.Text type="secondary">
          {row.family_label ? `${row.family_label} · ` : ''}
          {row.series_member_count.toLocaleString()}{' '}
          {row.series_member_count === 1 ? 'bond' : 'bonds'}
        </Typography.Text>
      </Space>
    ),
  },
  {
    title: 'Country',
    dataIndex: 'country_code',
    key: 'country_code',
    width: 100,
    sorter: (a, b) => compareText(a.country_code, b.country_code),
    filters: uniqueFilters(
      rows,
      row => row.country_code,
      row => row.country_code,
    ),
    filterMultiple: true,
    filterSearch: true,
    onFilter: (value, row) => value === row.country_code,
  },
  {
    title: 'Initial tenor',
    dataIndex: 'initial_tenor',
    key: 'initial_tenor',
    width: 125,
    sorter: (a, b) => a.initial_tenor_years - b.initial_tenor_years,
    filters: uniqueFilters(
      rows,
      row => row.initial_tenor_years,
      row => row.initial_tenor,
    ),
    filterMultiple: true,
    filterSearch: true,
    onFilter: (value, row) => value === row.initial_tenor_years,
  },
  {
    title: 'Currency',
    dataIndex: 'currency_search',
    key: 'currency',
    width: 130,
    sorter: (a, b) => compareText(a.currency.symbol, b.currency.symbol),
    filters: uniqueFilters(
      rows,
      row => row.currency.id,
      row => `${row.currency.symbol} — ${row.currency.name}`,
    ),
    filterMultiple: true,
    filterSearch: true,
    onFilter: (value, row) => value === row.currency.id,
    render: (_value, row) => row.currency.symbol,
  },
  {
    title: 'Reference curve',
    dataIndex: 'reference_curve_search',
    key: 'reference_curve',
    width: 190,
    sorter: (a, b) =>
      compareText(a.reference_curve.label, b.reference_curve.label),
    filters: uniqueFilters(
      rows,
      row => row.reference_curve.id,
      row => `${row.reference_curve.code} — ${row.reference_curve.label}`,
    ),
    filterMultiple: true,
    filterSearch: true,
    onFilter: (value, row) => value === row.reference_curve.id,
    render: (_value, row) => row.reference_curve.label,
  },
  {
    title: 'UnderlyingAsset ID',
    dataIndex: 'underlying_asset_id',
    key: 'underlying_asset_id',
    width: 165,
    sorter: (a, b) =>
      compareOptionalNumber(a.underlying_asset_id, b.underlying_asset_id),
    ...columnSearchWidget({ dataIndex: 'underlying_asset_id' }),
    render: display,
  },
  {
    title: 'Bond',
    dataIndex: 'asset_search',
    key: 'asset',
    width: 240,
    sorter: (a, b) => compareText(a.name, b.name),
    ...columnSearchWidget({ dataIndex: 'asset_search' }),
    render: (_value, row) =>
      row.underlying_asset_id === null ? (
        <Typography.Text type="secondary">No generated bonds</Typography.Text>
      ) : (
        <Space orientation="vertical" size={0}>
          <span>{display(row.name)}</span>
        </Space>
      ),
  },
  {
    title: 'Ticker',
    dataIndex: 'ticker',
    key: 'ticker',
    width: 130,
    sorter: (a, b) => compareText(a.ticker, b.ticker),
    ...columnSearchWidget({ dataIndex: 'ticker' }),
    render: display,
  },
  {
    title: 'RIC',
    dataIndex: 'ric',
    key: 'ric',
    width: 145,
    sorter: (a, b) => compareText(a.ric, b.ric),
    ...columnSearchWidget({ dataIndex: 'ric' }),
    render: display,
  },
  {
    title: 'ISIN',
    dataIndex: 'isin',
    key: 'isin',
    width: 150,
    sorter: (a, b) => compareText(a.isin, b.isin),
    ...columnSearchWidget({ dataIndex: 'isin' }),
    render: display,
  },
  {
    title: 'Issue date',
    dataIndex: 'legal_issue_date',
    key: 'legal_issue_date',
    width: 125,
    sorter: (a, b) => compareText(a.legal_issue_date, b.legal_issue_date),
    defaultSortOrder: 'descend',
    ...columnSearchWidget({ dataIndex: 'legal_issue_date' }),
    render: display,
  },
  {
    title: 'Maturity date',
    dataIndex: 'legal_maturity_date',
    key: 'legal_maturity_date',
    width: 135,
    sorter: (a, b) => compareText(a.legal_maturity_date, b.legal_maturity_date),
    ...columnSearchWidget({ dataIndex: 'legal_maturity_date' }),
    render: display,
  },
  {
    title: 'EOD from',
    dataIndex: 'priced_from',
    key: 'priced_from',
    width: 120,
    sorter: (a, b) => compareText(a.priced_from, b.priced_from),
    ...columnSearchWidget({ dataIndex: 'priced_from' }),
    render: display,
  },
  {
    title: 'EOD through',
    dataIndex: 'priced_through',
    key: 'priced_through',
    width: 130,
    sorter: (a, b) => compareText(a.priced_through, b.priced_through),
    ...columnSearchWidget({ dataIndex: 'priced_through' }),
    render: display,
  },
  {
    title: 'EOD MarketHistory',
    dataIndex: 'eod_search',
    key: 'eod_market_history',
    fixed: 'right',
    width: 190,
    sorter: (a, b) => a.market_history_row_count - b.market_history_row_count,
    ...columnSearchWidget({ dataIndex: 'eod_search' }),
    render: (_value, row) => (
      <Space>
        <span>{row.market_history_row_count.toLocaleString()} rows</span>
        <Tooltip title="Preview all EOD MarketHistory">
          <Button
            aria-label={`Preview EOD MarketHistory for ${
              row.ric || row.series_code
            }`}
            disabled={
              row.underlying_asset_id === null ||
              row.market_history_row_count === 0
            }
            icon={<EyeOutlined />}
            loading={previewingAssetId === row.underlying_asset_id}
            onClick={() =>
              row.underlying_asset_id !== null &&
              previewEod(row.underlying_asset_id)
            }
            size="small"
          >
            Preview
          </Button>
        </Tooltip>
      </Space>
    ),
  },
  {
    title: 'Intraday MarketHistory',
    key: 'intraday_market_history',
    fixed: 'right',
    width: 120,
    render: (_value, row) => (
      <Tooltip title="Preview the latest 1,000 stored intraday rows, including prices and yields">
        <Button
          aria-label={`Preview intraday MarketHistory for ${row.ric || row.series_code}`}
          disabled={
            !previewIntraday ||
            row.underlying_asset_id === null ||
            previewingIntradayAssetId !== undefined
          }
          icon={<EyeOutlined />}
          loading={previewingIntradayAssetId === row.underlying_asset_id}
          onClick={() =>
            row.underlying_asset_id !== null &&
            previewIntraday?.(row.underlying_asset_id)
          }
          size="small"
        >
          Preview
        </Button>
      </Tooltip>
    ),
  },
];

export const syntheticBondCatalogueCsvHeaders = [
  { label: 'Series', key: 'series_code' },
  { label: 'Series active', key: 'series_active' },
  { label: 'Series family label', key: 'family_label' },
  { label: 'Series member count', key: 'series_member_count' },
  { label: 'Country', key: 'country_code' },
  { label: 'Initial tenor', key: 'initial_tenor' },
  { label: 'Currency', key: 'currency' },
  { label: 'Reference curve code', key: 'reference_curve_code' },
  { label: 'Reference curve', key: 'reference_curve_label' },
  { label: 'UnderlyingAsset ID', key: 'underlying_asset_id' },
  { label: 'Bond name', key: 'name' },
  { label: 'Ticker', key: 'ticker' },
  { label: 'RIC', key: 'ric' },
  { label: 'ISIN', key: 'isin' },
  { label: 'Issue date', key: 'legal_issue_date' },
  { label: 'Maturity date', key: 'legal_maturity_date' },
  { label: 'EOD from', key: 'priced_from' },
  { label: 'EOD through', key: 'priced_through' },
  { label: 'EOD MarketHistory rows', key: 'market_history_row_count' },
];

export const syntheticBondCatalogueCsvRows = (
  rows: SyntheticBondCatalogueTableRow[],
) =>
  rows.map(row => ({
    series_code: row.series_code,
    series_active: row.series_active ? 'Yes' : 'No',
    family_label: row.family_label,
    series_member_count: row.series_member_count,
    country_code: row.country_code,
    initial_tenor: row.initial_tenor,
    currency: row.currency.symbol,
    reference_curve_code: row.reference_curve.code,
    reference_curve_label: row.reference_curve.label,
    underlying_asset_id: row.underlying_asset_id ?? '',
    name: row.name ?? '',
    ticker: row.ticker ?? '',
    ric: row.ric ?? '',
    isin: row.isin ?? '',
    legal_issue_date: row.legal_issue_date ?? '',
    legal_maturity_date: row.legal_maturity_date ?? '',
    priced_from: row.priced_from ?? '',
    priced_through: row.priced_through ?? '',
    market_history_row_count: row.market_history_row_count,
  }));

const SyntheticBondCatalogue: React.FC = () => {
  const dispatch = useDispatch();
  const { catalogue, catalogueError, catalogueFetching } = useSelector(
    selectors.syntheticBondGeneration,
  );
  const assetEodData = useSelector(selectors.assetEodData);
  const assetIntradayData = useSelector(selectors.assetIntradayData);
  const [previewingAssetId, setPreviewingAssetId] = useState<number>();
  const [showEodPreview, setShowEodPreview] = useState(false);
  const [previewingIntradayAssetId, setPreviewingIntradayAssetId] =
    useState<number>();
  const [showIntradayPreview, setShowIntradayPreview] = useState(false);
  const [intradayPreviewError, setIntradayPreviewError] = useState(false);

  useEffect(() => {
    if (!catalogue && !catalogueFetching && !catalogueError) {
      dispatch(fetchSyntheticBondCatalogue({}));
    }
  }, [catalogue, catalogueError, catalogueFetching, dispatch]);

  useEffect(() => {
    if (previewingAssetId !== undefined && assetEodData) {
      setPreviewingAssetId(undefined);
      setShowEodPreview(true);
    }
  }, [assetEodData, previewingAssetId]);

  useEffect(() => {
    if (previewingIntradayAssetId !== undefined && assetIntradayData) {
      if (assetIntradayData.asset_id === previewingIntradayAssetId) {
        setPreviewingIntradayAssetId(undefined);
        setShowIntradayPreview(true);
      } else if (!assetIntradayData.ric) {
        setPreviewingIntradayAssetId(undefined);
        setIntradayPreviewError(true);
      }
    }
  }, [assetIntradayData, previewingIntradayAssetId]);

  useEffect(
    () => () => {
      dispatch(clearAssetEodPreview({}));
      dispatch(clearAssetIntradayPreview({}));
    },
    [dispatch],
  );

  const rows = useMemo(() => catalogueTableRows(catalogue || []), [catalogue]);
  const csvRows = useMemo(() => syntheticBondCatalogueCsvRows(rows), [rows]);
  const columns = useMemo(
    () =>
      syntheticBondCatalogueColumns(
        assetId => {
          dispatch(clearAssetEodPreview({}));
          setShowEodPreview(false);
          setPreviewingAssetId(assetId);
          dispatch(requestAssetEodPreview({ asset_id: assetId }));
        },
        previewingAssetId,
        rows,
        assetId => {
          dispatch(clearAssetIntradayPreview({}));
          setShowIntradayPreview(false);
          setIntradayPreviewError(false);
          setPreviewingIntradayAssetId(assetId);
          dispatch(
            requestAssetIntradayPreview({
              asset_id: assetId,
              latest_rows: 1000,
            }),
          );
        },
        previewingIntradayAssetId,
      ),
    [dispatch, previewingAssetId, previewingIntradayAssetId, rows],
  );

  return (
    <section>
      {showEodPreview && assetEodData ? (
        <AssetEodPreviewModal
          visible={showEodPreview}
          hide={() => setShowEodPreview(false)}
        />
      ) : null}
      {showIntradayPreview && assetIntradayData ? (
        <AssetIntradayPreviewModal
          visible={showIntradayPreview}
          hide={() => setShowIntradayPreview(false)}
        />
      ) : null}

      <Space
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div>
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            Synthetic bond catalogue
          </Typography.Title>
          <Typography.Text type="secondary">
            One row per generated Series member; defined empty Series are also
            shown.
          </Typography.Text>
        </div>
        <Space>
          <CSVLink
            data={csvRows}
            filename="synthetic_bond_catalogue.csv"
            headers={syntheticBondCatalogueCsvHeaders}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <Button icon={<DownloadOutlined />}>Download CSV</Button>
          </CSVLink>
          <Button
            icon={<ReloadOutlined />}
            loading={catalogueFetching}
            onClick={() => dispatch(fetchSyntheticBondCatalogue({}))}
          >
            Refresh
          </Button>
        </Space>
      </Space>

      {catalogueError ? (
        <Alert
          action={
            <Button
              onClick={() => dispatch(fetchSyntheticBondCatalogue({}))}
              size="small"
            >
              Retry
            </Button>
          }
          message="Synthetic bond catalogue could not be loaded"
          description={catalogueError}
          showIcon
          style={{ marginBottom: 16 }}
          type="error"
        />
      ) : null}
      {intradayPreviewError ? (
        <Alert
          message="Intraday preview could not be loaded. Please try Preview again."
          showIcon
          style={{ marginBottom: 16 }}
          type="error"
        />
      ) : null}

      <Table
        columns={columns}
        dataSource={rows}
        loading={catalogueFetching}
        locale={{ emptyText: 'No synthetic bond Series have been defined.' }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        rowKey="row_id"
        scroll={{ x: 2310 }}
        size="small"
      />
    </section>
  );
};

export default SyntheticBondCatalogue;
